import { Link } from "react-router-dom";
import { DownloadIcon } from "@chakra-ui/icons";
import { Box, Divider, Flex, Heading, IconButton, List, ListItem } from "@chakra-ui/react";
import OverviewTimeline from "../../pipelineImport/components/OverviewTimeline";
import downloadFile from "../../../api/downloadFile";
import { Reconciliation } from "../../../api/types";
import { typedSxMap } from "../../../utils/typedSxMap";

const styles = typedSxMap({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "var(--m-xl)",
  },
  sectionHeading: {
    fontSize: "var(--font-size-xl)",
    color: "var(--color-text-strong)",
  },
  divider: {
    borderColor: "var(--color-border)",
    margin: "var(--m-s) 0",
  },
  summary: {
    display: "flex",
    gap: "var(--m-xl)",
    marginTop: "var(--m-l)",
  },
  summaryItem: {
    display: "flex",
    flexDirection: "column",
    gap: "var(--m-xs)",
  },
  summaryItemValue: {
    fontSize: "var(--font-size-xl)",
    fontWeight: 600,
  },
  details: {
    display: "flex",
    flexDirection: "column",
    gap: "var(--m-xxxs)",
  },
  detailsItem: {
    display: "flex",
    justifyContent: "",
    color: "var(--color-text-strong)",

    "& > span": {
      textAlign: "left",
      "&:first-of-type": {
        maxWidth: "350px",
        color: "var(--color-text-soft)",
        width: "50%",
      },
    },
  },
  firstBox: {
    marginTop: "-20px",
  },
  importNameContainer: {
    display: "flex",
    alignItems: "center",
    gap: "var(--m-xs)",
  },
  downloadButton: {
    minWidth: "auto",
    height: "24px",
    width: "24px",
  },
});

type OverviewSectionProps = {
  reconciliation: Reconciliation;
};

export default function OverviewSection({ reconciliation }: OverviewSectionProps) {
  const handleDownload = (importId?: string) => {
    if (importId) {
      downloadFile(importId, "original");
    }
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.firstBox}></Box>
      <Box>
        <Heading as="h3" sx={styles.sectionHeading}>
          Details
        </Heading>
        <Divider sx={styles.divider} />
        <List sx={styles.details}>
          <ListItem sx={styles.detailsItem}>
            <span>Purchase Order</span>
            <Flex sx={styles.importNameContainer}>
              <Link to={`/import/${reconciliation?.pipeline_import_1?.id}`}>
                <span>{reconciliation?.pipeline_import_1?.name || "-"}</span>
              </Link>
              {reconciliation?.pipeline_import_1?.id && (
                <IconButton
                  aria-label="Download original file"
                  icon={<DownloadIcon />}
                  size="xs"
                  sx={styles.downloadButton}
                  onClick={() => handleDownload(reconciliation?.pipeline_import_1?.id)}
                />
              )}
            </Flex>
          </ListItem>
          <ListItem sx={styles.detailsItem}>
            <span>Packing List</span>
            <Flex sx={styles.importNameContainer}>
              <Link to={`/import/${reconciliation?.pipeline_import_2?.id}`}>
                <span>{reconciliation?.pipeline_import_2?.name || "-"}</span>
              </Link>
              {reconciliation?.pipeline_import_2?.id && (
                <IconButton
                  aria-label="Download original file"
                  icon={<DownloadIcon />}
                  size="xs"
                  sx={styles.downloadButton}
                  onClick={() => handleDownload(reconciliation?.pipeline_import_2?.id)}
                />
              )}
            </Flex>
          </ListItem>
          {/* Add more details as needed */}
        </List>
      </Box>

      <Box>
        <Heading as="h3" sx={styles.sectionHeading}>
          Timeline
        </Heading>
        <Divider sx={styles.divider} />
        <OverviewTimeline timelineData={reconciliation?.status_history || []} />
      </Box>
    </Box>
  );
}
